import AbstractPiniaModel from './AbstractPiniaModel'
import {TestState, Answer} from 'Models'
import QuestionRealTestStrategy from './strategies/QuestionRealTestStrategy'
import QuestionExerciseStrategy from './strategies/QuestionExerciseStrategy'
import { useQuestionsStore } from '../pinia/stores/questions'
import {questionTypes} from 'Shared'
import clamp from 'ramda/es/clamp'
import either from 'ramda/es/either'
import isNil from 'ramda/es/isNil'
import isEmpty from 'ramda/es/isEmpty'

//Todo: Move to appropriate place
const addMediaBaseUrlTo = (file='') => window.location.origin + '/storage/media/' + file

export class Question extends AbstractPiniaModel {
    //#region Instance Code
        constructor(question) { 
            super(question)
            if (question && this.source && !this.source.marked) this.source.marked = false  
        }

        id()                    { return this.source.id }
        type()                  { return this.source.aufgaben_typ }
        question()              { return this.source.aufgabenstellung }
        questionSanitized()     { return this.getByAliases(['code_freie_aufgabenstellung'], '') }
        solutionExplanation()   { return this.source.loesungs_kommentar }
        difficulty()            { return this.source.schwierigkeit }
        duration()              { return this.source.dauer }
        lastAnswer()            { return this.source.last_answer }
        setLastAnswer(a)        { this.source.last_answer = a }
        isSaveAnswer()            { return this.source.save_answer }
        isRecording()            { return this.source.recording }

        durationBetweenOptions(pace='standard')
        {
            let standardDuration = this.getByAliases(['dauer_pro_frage_in_millisek'], 0)

            if(pace==='slow')
                standardDuration = this.getByAliases(['langsame_dauer_pro_frage_in_millisek'], standardDuration + 300)

            else if(pace==='fast')
                standardDuration = this.getByAliases(['schnelle_dauer_pro_frage_in_millisek'], standardDuration - 300)

            standardDuration = Number(standardDuration)

            return standardDuration<0 ? 0 : standardDuration
        }

        answerOptions()
        {
            try
            {
                 return JSON.parse(this.source.antwort_optionen)
            }
            catch(ex)
            {
                return this.source.antwort_optionen || []
            }
        }

        countOfOptionsToShow()
        {
            return Number(this.getByAliases(['anzahl_optionen'], 0))
        }

        widthPerOption()
        {
            return Number(this.getByAliases(['breite_pro_option'], 0))
        }

        correctAnswers()
        {
            const result = this.getByAliases(['antworten'], [])

            try
            {
                return JSON.parse(result)
            }
            catch (e)
            {
                return result
            }
        }

        displayHorizontally()   { return this.source.horizontale_darstellung }
        lastAnsweredAt()        { return this.source.lastAnsweredAt }
        countPerAnswer()        { return this.source.wertung !== 'gesamt'}
        lineEvaluation()        { return this.source.wertung === 'zeile'}

        points()
        {
            const alt = this.isSelfRated() ? [{punkte:0, bewertung:"falsch"}] : 0

            return JSON.parse(this.getByAliases(['punkte'], alt))
        }

        latestScore()           { return this.getByAliases(['scoredPoints'], 0) }
        titleRowValues()        { return this.source.antwort_optionen.titelzeile }
        blockNumber()           { return clamp(0, this.source.block+10, this.source.block-1) }
        blockPosition()         { return this.source.block_position }
        randomBlockSort()       { return this.source.block_sortierung==='random' }
        dictationType()         { return this.getByAliases(['diktat_typ'], 'synchron') }
        media() {
            try {
                if (!this.cachedMedia) {
                    this.cachedMedia = this.getByAliases(['media'], []).map(addMediaBaseUrlTo)
                }
                return this.cachedMedia
            }
            catch (ex) {
                return null
            }
        }

        realQuestionsCount()    { return Number(this.source.anzahl_fragen || 0) }
        realCorrectCount()      {
            if(this.isUnedited()) return 0

            if(!this.countPerAnswer())
                return this.isAnsweredCorrectly() ? this.realQuestionsCount() : 0
            
            // special treatment for multiple answer questions
            // if the question is only partially answered correctly, it still counts as incorrect
            if (this.isMultipleAnswer() && this.latestScore() != this.points()) {
                return 0;
            }

            // In Freitext this.points() returns a JSON structure and would fail.
            // However, there are no Freitext question that count more than one!
            if( Number.isNaN(Number(this.points())) )
                return this.isAnsweredCorrectly() ? 1 : 0
            return this.latestScore() / Number(this.points())
        }
        realWrongCount()        {
            if(this.isUnedited()) return 0
            return this.realQuestionsCount() - this.realCorrectCount()
        }
        realUneditedCount()     {
            return this.isUnedited() ? this.realQuestionsCount() : 0
        }
        updateRealCounts() {
            Question.dispatch('setRealNumOfWrongQuestions', { id: this.id(), num: this.realWrongCount() })
            Question.dispatch('setRealNumOfCorrectQuestions', { id: this.id(), num: this.realCorrectCount() })
        }


        interactionAreaStyles() { return this.getByAliases(['fragenbereich_css'], 'input-white') }

        mainCategoryID()        { return this.source.mainCategoryID }
        subCategoryID()         { return this.source.subCategoryID }
        subCategoryName() {
            return this.getByAliases(['subCategoryName', 'sub_category_name'], '')
        }
        insertedFromSubCategoryID() {
            return this.getByAliases(
                ['insertedFromSubCategoryID', 'insertedFromSubCategoryId', 'inserted_from_sub_category_id'],
                0
            )
        }

        isDictationType(type)   { return this.dictationType() === type }
        hasMedia()              { return !(either(isNil, isEmpty)(this.media())) }

        isMultipleChoice()      { return this.type() === questionTypes.multipleChoice }
        isMultipleAnswer()      { return this.type() === questionTypes.multipleAnswer }
        isLueckentext()         { return this.type() === questionTypes.lueckentext }
        isFreitext()            { return this.type() === questionTypes.freitext }
        isNurText()             { return this.type() === questionTypes.nurText }
        isClickSvg()            { return this.type() === questionTypes.clicksvg }
        isClickMatrix()         { return this.type() === questionTypes.clickmatrix }
        isDnDListSort()         { return this.type() === questionTypes.dndlistsort }
        isDnDGroupSort()        { return this.type() === questionTypes.dndgroupsort }
        isDnD1u1Zuordnungen()   { return this.type() === questionTypes.dnd1u1zuordnungen }

        isCTWebertest()             { return this.type() === questionTypes.ctWebertest }
        isFrankfurterAdaptiveTest() { return this.type() === questionTypes.frankfurterAdaptiveTest }
        isMilitaryReactionTest()    { return this.type() === questionTypes.militaryReactionTest }
        isFlexibleReactionTest()    { return this.type() === questionTypes.flexibleReactionTest }
        isPersonalityTest()    { return this.type() === questionTypes.personalityTest }
        isProfessionTypeTest()    { return this.type() === questionTypes.professionTypeTest }

        hasSpeedChoice()        { return this.isReactionTest() }
        isReactionTest()
        {
            return  this.isCTWebertest() ||
                    this.isFrankfurterAdaptiveTest() ||
                    this.isMilitaryReactionTest() ||
                    this.isFlexibleReactionTest()
        }


        isExample()           { return this.source.ist_beispielaufgabe }
        isSelfRated()           { return this.isFreitext() }
        isMarked()              { return this.getByAliases(['marked'], false) }
        isEdited()              { return !this.isUnedited() }
        isUnedited()            { return this.getByAliases(['unedited'], true) }
        isAnsweredCorrectly()   { return this.getByAliases(['answeredCorrectly'], false) }
        isModified()            { return this.getByAliases(['modified_answers'], false) }
        doesOrderCount()        { return !this.getByAliases(['reihenfolge_egal'], false) }
        isFirstInBlock()        {
            return Question.selectedIndex()<=0 ||
                   Question.previousQuestion().blockNumber() < this.blockNumber()
        }
        isActive()              { return !Boolean(this.source.deleted_at) }

        showTitleRow()          { return this.source.antwort_optionen.titelzeile.length > 0 }
        showTitleCol()          { return this.source.antwort_optionen.titelspalte.toLowerCase()==="true" }

        setToEdited()               { Question.dispatch('setUneditedTo', { id: this.id(), value:false }) }
        setAnsweredCorrectlyTo(val) { Question.dispatch('setAnsweredCorrectlyTo', { id: this.id(), value:val }) }
        setModifiedTo(val)          { Question.dispatch('setModifiedTo', { id: this.id(), value:val }) }
        setScoredPointsTo(val)      { Question.dispatch('setScoredPointsTo', { id: this.id(), value:val }) }

    //#endregion Instance Code


    //#region Static Methods
        static piniaStore()                 { return useQuestionsStore() }

        static create(questionSource)       { return new Question(questionSource) }

        static strategy()                   { return TestState.isRealTest() ? QuestionRealTestStrategy
                                                                            : QuestionExerciseStrategy }

                                                                            //Todo: Implement below strategy method
        static isFirstQuestionOverall()     { return this.strategy().isFirstQuestionOverall() }
        static isFirstQuestionInBlock()     { return this.strategy().isFirstQuestionInBlock() }
        static isLastQuestionReached()      { return this.strategy().isLastQuestionReached() }
        static doQuestionsExistFor(mainCategoryID, subCategoryID) {
            return this.strategy().doQuestionsExistFor(mainCategoryID, subCategoryID)
        }
        static areCorrectQuestionsVisible()     { return this.piniaStore().showCorrectQuestions }
        static areWrongQuestionsVisible()       { return this.piniaStore().showWrongQuestions }
        static areUneditedQuestionsVisible()    { return this.piniaStore().showUneditedQuestions }
        static getSelectedSortOption()          { return this.piniaStore().selectedSortOption }

        static selectedIndex()                  { return this.strategy().selectedIndex() }
        static currentQuestionsCount()          { return this.strategy().currentQuestionsCount() }
        static currentQuestionsDuration()       { return this.strategy().currentQuestionsDuration() }
        static currentQuestion()                { return this.strategy().currentQuestion() }
        static previousQuestion()               { return this.strategy().previousQuestion() }
        static currentQuestions()               { return this.strategy().currentQuestions() }
        static currentQuestionsSubCategories()  { return this.strategy().currentQuestionsSubCategories()}
        static allQuestions()  { return this.strategy().allQuestions()}
        static allQuestionsCount()  { return this.strategy().allQuestionsCount()}


        static ignoreCurrentQuestion()          { this.strategy().ignoreCurrentQuestion() }
        static incSelectedIndex()               { this.strategy().incSelectedIndex() }
        static decSelectedIndex()               { this.strategy().decSelectedIndex() }
        static setSelectedIndexTo(index)        { this.strategy().setSelectedIndexTo(index) }
        static setCurrentQuestions(questions)   { this.strategy().setCurrentQuestions(questions) }
        static setActiveQuestions(questions)    { this.strategy().setActiveQuestions(questions) }
        static setShowCorrectQuestionsTo(value) { this.dispatch('setShowCorrectQuestionsTo', value) }
        static setShowWrongQuestionsTo(value)   { this.dispatch('setShowWrongQuestionsTo', value) }
        static setShowUneditedQuestionsTo(value){ this.dispatch('setShowUneditedQuestionsTo', value) }
        static setSelectedSortOption(value)    {  this.dispatch('setSelectedSortOption', value) }

        static sortCurrentQuestions(sort) {
            const questions = this.currentQuestions();
            const isInsertQuestion = (question) => {
                const insertedFromSubCategoryID = typeof question.insertedFromSubCategoryID === 'function'
                    ? Number(question.insertedFromSubCategoryID() || 0)
                    : Number(question.source?.insertedFromSubCategoryID || question.source?.inserted_from_sub_category_id || 0)

                return insertedFromSubCategoryID > 0
            }
            const insertedQuestions = questions.filter(isInsertQuestion)
            const sourceQuestions = questions.filter((question) => !isInsertQuestion(question))
            const mapSources = (sortedQuestions) => sortedQuestions.map((question) => question.getSource())

            switch(sort) {
                case 'random':
                    this.setActiveQuestions(mapSources(insertedQuestions.concat(
                        sourceQuestions.sort(() => Math.random() - 0.5)
                    )))
                    break
                case 'easy2difficult':
                    this.setActiveQuestions(mapSources(insertedQuestions.concat(
                        sourceQuestions.sort((a, b) => (a.difficulty() > b.difficulty() ? -1 : 1))
                    )))
                    break
                case 'difficult2easy':
                    this.setActiveQuestions(mapSources(insertedQuestions.concat(
                        sourceQuestions.sort((a, b) => (a.difficulty() < b.difficulty() ? -1 : 1))
                    )))
                    break
            }
        }

        static markByID(id, data)               {
            return TestState.isInResultsMode() ? Answer.markByID(id, data)
                                               : this.strategy().markByID(id, data)
        }
        static unmarkByID(id, data)             {
            return TestState.isInResultsMode() ? Answer.unmarkByID(id, data)
                                               : this.strategy().unmarkByID(id, data)
        }

        static resetAll()                       { this.strategy().resetAll() }

        static loadAsCurrentQuestionsFor(professionID, mainCategoryID, subCategoryID, hasBrowserReloaded=false) {
            return this.strategy().loadAsCurrentQuestionsFor(professionID, mainCategoryID, subCategoryID, hasBrowserReloaded)
        }

        static loadAsCurrentQuestionsFromLocalFor(mainCategoryID, subCategoryID, hasBrowserReloaded=false) {
            return this.strategy().loadAsCurrentQuestionsFromLocalFor(mainCategoryID, subCategoryID, hasBrowserReloaded)
        }
    //#endregion Static Methods
}
